<script setup lang="ts">
import { preloadComponents } from '#app'
import type { Game, GameClickEventEmit, GameListProps } from '@game/types'
import { useGameList } from '#imports'

const props = withDefaults(defineProps<GameListProps>(), {
    showAllButton: false,
    forceShowAllButton: false,
    showNewGames: true,
    hasSidebar: false,
    videoEnabled: true,
    enableJackpots: 'auto',
    maxColumns: 5,
})

const emit = defineEmits<GameClickEventEmit & {
    'load-more-games': [category: string]
}>()

// Provides list-scoped jackpot/promotion queries and a shared opening-hours
// clock to every <GameTile> rendered under this list — avoids each tile
// registering its own useAsyncData / useCountdown instances.
useGameList(() => props.games, {
    enableJackpots: () => props.enableJackpots,
})

await preloadComponents([props.inlineRows ? 'GameListInline' : 'GameListGrid'])
</script>

<template>
    <section
        class="game-list__section"
        :class="{
            'game-list--inline': props.inlineRows,
            'game-list--mini-tiles': props.inlineRows && props.miniTiles
        }"
    >
        <!-- eslint-disable-next-line vue/no-v-html props.title is safe -->
        <h1 v-if="props.title" class="game-list__title" v-html="props.title" />

        <GameListInline
            v-if="props.inlineRows"
            v-bind="props"
            @game-click="(game: Game) => emit('game-click', game)"
        />

        <GameListGrid
            v-else
            v-bind="props"
            @load-more-games="emit('load-more-games', props.category)"
            @game-click="(game: Game) => emit('game-click', game)"
        />
    </section>
</template>

<style lang="scss">
.game-list {
    --max-columns: 5;

    display: grid;
    grid-auto-flow: dense;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 10px;

    @media (min-width: 500px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @include tablet(md) {
        margin-bottom: 15px;
        grid-template-columns: repeat(4, 1fr);
    }

    @include desktop {
        grid-template-columns: repeat(calc(var(--max-columns) - 1), 1fr);
    }

    @include desktop-xl {
        grid-template-columns: repeat(var(--max-columns), 1fr);
    }

    &--has-sidebar {
        @include desktop {
            grid-template-columns: repeat(calc(var(--max-columns) - 2), 1fr);
        }

        @include desktop-xl {
            grid-template-columns: repeat(calc(var(--max-columns) - 1), 1fr);
        }
    }

    &--inline {
        .game-list {
            padding: 0 30px 0 0;
            margin-bottom: 10px;
            gap: 10px;
            grid-auto-flow: column;
            grid-auto-columns: max-content;
            grid-template-columns: max-content;
            overflow-x: auto;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            overflow-y: hidden;
            transform: translateZ(0);
            scrollbar-color: transparent transparent;
            scrollbar-width: none;

            ::-webkit-scrollbar {
                display: none;
            }

            &__container {
                position: relative;
                flex-direction: column;
                margin-right: -15px;

                @include desktop {
                    margin-right: 0;
                }
            }
        }

        .game-tile,
        .skeleton-item,
        .game-list__tile-slot {
            scroll-snap-type: start;
            width: 135px;
            height: 160px;

            @include desktop {
                width: 160px;
                height: 190px;
            }
        }

        .game-tile--video,
        .game-list__tile-slot--video {
            width: 280px;

            @include desktop {
                width: 330px;
            }
        }
    }

    &--mini-tiles {
        .game-tile,
        .skeleton-item,
        .game-list__tile-slot {
            width: 135px;
            height: 88px;

            @include desktop {
                width: 160px;
                height: 104px;
            }
        }
    }

    &__section {
        width: 100%;

        & + &,
        .custom-game-section + &,
        .games-with-sidebar + & {
            margin-top: 20px;

            @include tablet {
                margin-top: 30px;
            }
        }
    }

    &__title {
        font-weight: $text-bold;
        font-size: 20px;
        margin-bottom: 13px;
        font-family: $brand-font-family;

        @include mobile {
            font-size: 22px;
        }

        @include mobile(sm) {
            font-size: 24px;
        }

        @include tablet(md) {
            font-size: 30px;
        }
    }
}

.skeleton-item {
    background-color: $skeleton-color;
    position: relative;
    z-index: 1;
    border-radius: $game-tile-border-radius;
    overflow: hidden;
    aspect-ratio: 1;

    &--video {
        grid-column: span 2;
        aspect-ratio: 2 / 1;
    }

    // Blurry loading line
    &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        transform: translate3d(-100%, 0, 0);
        background-image: linear-gradient(90deg, transparent 0, $skeleton-loader-color 50%, transparent 100%);
        animation: skeleton-load 1.5s ease-in-out infinite;
    }
}

@keyframes skeleton-load {
    to {
        transform: translate3d(100%, 0, 0);
    }
}
</style>
